import React from "react";
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  Icon,
  IconName,
  Skeleton,
  styles as ui,
} from "stories/Admin";
import styles from "./HadithPage.module.scss";

/** Shared layout for «Dagens hadith» and «AI: dagens vers» (Koranen). */

export const formatShortDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString("nb-NO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

export const ConfidenceBadge: React.FC<{ confidence: number }> = ({
  confidence,
}) => {
  if (confidence >= 0.8) return <Badge tone="mint">Høy</Badge>;
  if (confidence >= 0.5) return <Badge tone="gold">Medium</Badge>;
  return <Badge tone="rose">Lav</Badge>;
};

interface DailyCardProps {
  title: React.ReactNode;
  badges: React.ReactNode;
  meta?: React.ReactNode;
  arabic: string;
  transliteration?: string;
  norwegian: string;
  explanation?: string;
  actionPoints?: string[];
  /** Extra content under the explanation (e.g. fiqh risk). */
  extra?: React.ReactNode;
  tags?: string[];
}

export const DailyCard: React.FC<DailyCardProps> = ({
  title,
  badges,
  meta,
  arabic,
  transliteration,
  norwegian,
  explanation,
  actionPoints,
  extra,
  tags,
}) => (
  <Card title={title}>
    <div className={styles.badges}>
      {badges}
      {meta && <span className={styles.meta}>{meta}</span>}
    </div>
    <p className={`${ui.arabic} ${styles.arabic}`} lang="ar">
      {arabic}
    </p>
    {transliteration && (
      <span className={styles.translit}>{transliteration}</span>
    )}
    <span className={styles.norwegian}>«{norwegian}»</span>
    {(explanation || (actionPoints && actionPoints.length > 0)) && (
      <div className={ui.g2}>
        <div>
          {explanation && (
            <>
              <span className={styles.label}>Forklaring</span>
              <p className={styles.text}>{explanation}</p>
            </>
          )}
        </div>
        <div>
          {actionPoints && actionPoints.length > 0 && (
            <>
              <span className={styles.label}>Handlingspunkter</span>
              <ul className={styles.points}>
                {actionPoints.map((point, index) => (
                  <li key={index}>
                    <span className={styles.check} aria-hidden="true">
                      <Icon name="check" size={12} strokeWidth={3} />
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    )}
    {extra}
    {tags && tags.length > 0 && (
      <div className={styles.badges}>
        {tags.map((tag, index) => (
          <Badge key={index} tone="lilac">
            {tag}
          </Badge>
        ))}
      </div>
    )}
  </Card>
);

export interface HistoryRow {
  key: React.Key;
  date: string;
  text: string;
  arabic?: string;
  transliteration?: string;
  confidence?: number;
  source: React.ReactNode;
}

interface DailyHistoryProps {
  title: React.ReactNode;
  rows: HistoryRow[];
  emptyIcon: IconName;
  emptyTitle: string;
  emptyText: string;
}

export const DailyHistory: React.FC<DailyHistoryProps> = ({
  title,
  rows,
  emptyIcon,
  emptyTitle,
  emptyText,
}) => (
  <Card title={title}>
    {rows.length === 0 ? (
      <EmptyState icon={emptyIcon} title={emptyTitle}>
        {emptyText}
      </EmptyState>
    ) : (
      <div>
        {rows.map((row) => (
          <div key={row.key} className={styles.historyRow}>
            <span className={styles.historyDate}>
              {formatShortDate(row.date)}
            </span>
            <span className={styles.historyText}>
              <span title={row.arabic}>«{row.text}»</span>
              {row.transliteration && (
                <span className={styles.translit}>{row.transliteration}</span>
              )}
            </span>
            {!!row.confidence && (
              <ConfidenceBadge confidence={row.confidence} />
            )}
            <span className={styles.historySource}>{row.source}</span>
          </div>
        ))}
      </div>
    )}
  </Card>
);

export const DailySkeleton: React.FC = () => (
  <div className={styles.loading}>
    <Card>
      <Skeleton width="40%" height={20} />
      <Skeleton height={44} />
      <Skeleton width="80%" />
      <Skeleton width="60%" />
    </Card>
    <Card>
      <Skeleton width="30%" height={20} />
      <Skeleton />
      <Skeleton />
      <Skeleton />
    </Card>
  </div>
);

export const DismissibleError: React.FC<{
  message: string | null;
  onClose: () => void;
}> = ({ message, onClose }) =>
  message ? (
    <Alert tone="error">
      {message}{" "}
      <button type="button" className={ui.linkBtn} onClick={onClose}>
        Lukk
      </button>
    </Alert>
  ) : null;
